import type { IProperty } from '@/domain/entities/property';
import { isNotFoundError } from '@/lib';
import { normalizeDeep } from '@/lib/dataSanity';
import { getApiEndpoints } from '@/services/api/endpoints';
import type { CreateItemResponse, PaginatedListResponse } from '@/services/api/types';
import { createAppAsyncThunk } from '@/store/typedAsyncThunk';
import type {
  ActivePropertyHandoverDetails,
  CancelPropertyHandoverPayload,
  CancelPropertyHandoverResponse,
  InitiatePropertyHandoverPayload,
  InitiatePropertyHandoverResponse,
  PropertyHandoverOperationType,
} from './propertyTypes';
import type { IPropertyEvent } from '@/domain/entities/property-event';
import { normalizePropertyCode, PROPERTY_CODE_PATTERN } from './propertyCode';

/**
 * Handover codes and claim links are bearer secrets, and a system super admin's Property reads
 * return the complete record. Never log these request/response payloads.
 */
const SENSITIVE_PROPERTY_REQUEST = { suppressLogging: true } as const;

function stripNonUpdatablePropertyFields(patch: Partial<IProperty>, options?: { keepId?: boolean }) {
  const propertyFields = { ...patch };
  if (!options?.keepId) delete propertyFields.id;
  delete propertyFields.typeName;
  delete propertyFields.createdAt;
  delete propertyFields.updatedAt;
  delete propertyFields.propertyCode;

  return propertyFields;
}

export const fetchUserProperties = createAppAsyncThunk<IProperty[]>(
  'properties/fetchProperties',
  async (_, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;

    try {
      const endpoint = getApiEndpoints().core.properties.getProperties;

      const properties = await apiService.get<IProperty[]>(endpoint, SENSITIVE_PROPERTY_REQUEST);
      return properties;
    } catch (err) {
      const error = err as Error & { status?: number; body?: string };
      return rejectWithValue({ message: error.message });
    }
  },
);

export const findPropertyByCode = createAppAsyncThunk<
  IProperty,
  string,
  { rejectValue: { message: string; status?: number } }
>('properties/findByCode', async (rawCode, { extra: { config }, rejectWithValue }) => {
  const propertyCode = normalizePropertyCode(rawCode);

  if (!PROPERTY_CODE_PATTERN.test(propertyCode)) {
    return rejectWithValue({ message: 'Enter a valid six-character property code' });
  }

  try {
    const params = new URLSearchParams({ propertyCode });
    const endpoint = `${getApiEndpoints().core.properties.getProperties}?${params.toString()}`;
    return await config.apiService.get<IProperty>(endpoint, SENSITIVE_PROPERTY_REQUEST);
  } catch (err) {
    const error = err as Error & { status?: number };

    if (error.status === 404) {
      return rejectWithValue({
        message: 'No accessible property was found for this code.',
        status: error.status,
      });
    }

    const statusSuffix = error.status ? ` (${error.status})` : '';
    return rejectWithValue({
      message: `Property lookup failed${statusSuffix}`,
      status: error.status,
    });
  }
});

export const fetchOrgProperties = createAppAsyncThunk<IProperty[], { orgID: string }>(
  'properties/listByOrg',
  async ({ orgID }, { extra: { config }, rejectWithValue }) => {
    const { apiService, logger } = config;

    try {
      const endpoint = getApiEndpoints().core.properties.getPropertiesByOrgID + `?orgID=${encodeURIComponent(orgID)}`;
      return await apiService.get<IProperty[]>(endpoint, SENSITIVE_PROPERTY_REQUEST);
    } catch (err) {
      const error = err as Error & { status?: number; body?: string };
      if (isNotFoundError(error)) {
        logger?.info('[fetchOrgProperties] No properties found for organization, returning empty list', { orgID });
        return [];
      }
      const e = err as Error & { status?: number; body?: string };
      return rejectWithValue({ message: e.message });
    }
  },
);
export interface FetchPropertyEventsParams {
  propertyID: string;
  limit?: number;
  nextToken?: string;
}

export const fetchPropertyEvents = createAppAsyncThunk<
  PaginatedListResponse<IPropertyEvent>,
  FetchPropertyEventsParams,
  { rejectValue: { message: string; status?: number; code?: string } }
>(
  'properties/listPropertyEvents',
  async ({ propertyID, limit = 20, nextToken }, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;

    try {
      const params = new URLSearchParams({
        propertyID,
        events: 'true',
        limit: String(limit),
      });

      if (nextToken) {
        params.append('nextToken', nextToken);
      }

      const endpoint = getApiEndpoints().core.properties.getPropertyEvents + `?${params.toString()}`;

      return await apiService.get<PaginatedListResponse<IPropertyEvent>>(endpoint);
    } catch (err) {
      const error = err as Error & { status?: number; body?: string };
      let backendMessage: string | undefined;

      if (error.body) {
        try {
          const parsed = JSON.parse(error.body);
          backendMessage = parsed.message;
        } catch {
          // ignore
        }
      }

      const isNoEvents = isNotFoundError(error);
      if (isNoEvents) {
        return {
          items: [],
          limit,
          total: 0,
        };
      }

      return rejectWithValue({
        message: backendMessage ?? error.message ?? 'Failed to fetch property events',
      });
    }
  },
);

export const fetchPropertyById = createAppAsyncThunk<IProperty, { id: string }>(
  'properties/getById',
  async ({ id }, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;

    try {
      const endpoint = `${getApiEndpoints().core.properties.getProperties}?id=${encodeURIComponent(id)}`;
      return await apiService.get<IProperty>(endpoint, SENSITIVE_PROPERTY_REQUEST);
    } catch (err) {
      const e = err as Error & { status?: number; body?: string };
      return rejectWithValue({ message: e.message });
    }
  },
);

export const createProperty = createAppAsyncThunk<CreateItemResponse<IProperty>, { property: IProperty }>(
  'properties/create',
  async ({ property }, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;

    try {
      const endpoint = getApiEndpoints().core.properties.createProperty;
      const normalized = normalizeDeep(stripNonUpdatablePropertyFields(property));

      return await apiService.put<CreateItemResponse<IProperty>>(endpoint, normalized);
    } catch (err) {
      const e = err as Error & { status?: number; body?: string };
      return rejectWithValue({ message: e.message });
    }
  },
);

export const updateProperty = createAppAsyncThunk<
  CreateItemResponse<IProperty>,
  { id: string; patch: Partial<IProperty> }
>('properties/update', async ({ id, patch }, { extra: { config }, rejectWithValue }) => {
  const { apiService } = config;

  try {
    const endpoint = `${getApiEndpoints().core.properties.updateProperty}?id=${encodeURIComponent(id)}`;
    const normalized = normalizeDeep(stripNonUpdatablePropertyFields(patch, { keepId: true }));
    return await apiService.put<CreateItemResponse<IProperty>>(endpoint, normalized, SENSITIVE_PROPERTY_REQUEST);
  } catch (err) {
    const e = err as Error & { status?: number; body?: string };
    return rejectWithValue({ message: e.message });
  }
});

export const initiatePropertyHandover = createAppAsyncThunk<
  InitiatePropertyHandoverResponse,
  InitiatePropertyHandoverPayload
>('properties/handoverInitiate', async (payload, { extra: { config }, rejectWithValue }) => {
  const { apiService } = config;

  try {
    const endpoint = getApiEndpoints().core.propertyHandover.initiate;

    // You said you route by operationType inside the handler
    const eventType: PropertyHandoverOperationType = 'INITIATE';
    const body = normalizeDeep({
      operationType: eventType,
      ...payload,
    });

    // business endpoint -> POST
    return await apiService.post<InitiatePropertyHandoverResponse>(endpoint, body, SENSITIVE_PROPERTY_REQUEST);
    // await sleep(1800); // simulate network delay
    // return mockedInitiatePropertyHandoverResponse;
  } catch (err) {
    const e = err as Error & { status?: number; body?: string };

    // Optional: parse backend JSON error body if you return { message/code/logs }
    let msg = e.message;
    if (e.body) {
      try {
        const parsed = JSON.parse(e.body);
        msg = parsed?.message || parsed?.error || parsed?.code || msg;
      } catch {
        // ignore
      }
    }

    return rejectWithValue({ message: msg });
  }
});

export const cancelPropertyHandover = createAppAsyncThunk<
  CancelPropertyHandoverResponse,
  CancelPropertyHandoverPayload
>('properties/handoverCancel', async (payload, { extra: { config }, rejectWithValue }) => {
  const { apiService } = config;

  try {
    const endpoint = getApiEndpoints().core.propertyHandover.cancel;

    const eventType: PropertyHandoverOperationType = 'CANCEL';
    const body = normalizeDeep({
      operationType: eventType,
      ...payload,
    });

    return await apiService.post<CancelPropertyHandoverResponse>(endpoint, body, SENSITIVE_PROPERTY_REQUEST);
  } catch (err) {
    const e = err as Error & { status?: number; body?: string };

    let msg = e.message;
    if (e.body) {
      try {
        const parsed = JSON.parse(e.body);
        msg = parsed?.message || parsed?.error || parsed?.code || msg;
      } catch {
        // ignore
      }
    }
    return rejectWithValue({ message: msg });
  }
});

/**
 * Reloads the active builder handover (code, claim links, expiry) after a refresh. The API
 * authorizes it with `properties:handover`; ordinary Property reads never carry the code.
 * Resolves to null when the Property has no active handover.
 */
export const fetchActivePropertyHandover = createAppAsyncThunk<
  ActivePropertyHandoverDetails | null,
  { propertyID: string }
>('properties/handoverActive', async ({ propertyID }, { extra: { config }, rejectWithValue }) => {
  const { apiService } = config;

  try {
    const endpoint = getApiEndpoints().core.propertyHandover.active;
    const eventType: PropertyHandoverOperationType = 'GET_ACTIVE_HANDOVER';
    return await apiService.post<ActivePropertyHandoverDetails>(
      endpoint,
      { operationType: eventType, propertyID },
      SENSITIVE_PROPERTY_REQUEST,
    );
  } catch (err) {
    const e = err as Error & { status?: number; body?: string };
    if (isNotFoundError(e)) {
      return null;
    }
    return rejectWithValue({ message: e.message });
  }
});
